import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button, Loader, Segment } from 'semantic-ui-react';
import { Notification, STATUS_TYPES, generateResolver, yup } from 'dyl-components';

import onboardingActions from 'actions/onboarding';
import companyActions from 'actions/company';
import timezoneOptions from 'pages/Schedule/timezoneOptions';
import { useConfirm } from 'shared/confirmation/useConfirm';
import BusinessHoursScreen from './BusinessHours';
import RoutingAndVoicemail from './RoutingAndVoicemail';
import ConfirmPhoneSetup from './ConfirmPhoneSetup';
import { summarizeBusinessHours } from './hoursUtils';
import { PHONE_SYSTEM_SCREENS } from '../constants';

const DEFAULT_VALUES = {
    timezone: '',
    business_hours: [],
    routing: { team_id: '', ring_strategy: 'ringall', ring_time: 30, no_answer_destination: 'company_voicemail' },
    ivr: { enabled: false, greeting_script: '', options: [] },
    voicemail: { greeting_script: '' }
};

const BUSINESS_HOURS_FIELDS = ['timezone', 'business_hours'];
const ROUTING_FIELDS = ['routing', 'ivr', 'voicemail'];

const getDuplicateIndexes = (values) => values.reduce((duplicates, value, index) => (
    value && values.indexOf(value) !== index ? [...duplicates, index] : duplicates
), []);

const PhoneSystemStep = ({ businessProfile = {}, teams = [], draft, onDraftChange, onBack, onComplete }) => {
    const dispatch = useDispatch();
    const { isConfirmed } = useConfirm();

    const { user_id, companyTimezone, isCompanyLoaded, isGeneratingPhoneSetup, isDraftingGreetings } = useSelector(state => ({
        user_id: state.auth.user_id,
        companyTimezone: state.company.company?.timezone,
        isCompanyLoaded: !!state.company.company,
        isGeneratingPhoneSetup: state.onboarding.isGeneratingPhoneSetup,
        isDraftingGreetings: state.onboarding.isDraftingGreetings
    }));

    const [screen, setScreen] = useState(draft?.approval ? PHONE_SYSTEM_SCREENS.CONFIRM : (draft?.screen || PHONE_SYSTEM_SCREENS.HOURS));
    const [explanations, setExplanations] = useState(draft?.explanations || {});
    const [generatedFor, setGeneratedFor] = useState(draft?.generated_for || null);
    const [approval, setApproval] = useState(draft?.approval || null);

    const { control, getValues, setValue, watch, trigger } = useForm({
        mode: 'onChange',
        defaultValues: draft?.values || DEFAULT_VALUES,
        resolver: generateResolver({
            timezone: yup.string().required('This field is required'),
            business_hours: yup.array().of(yup.object({
                active: yup.boolean(),
                seconds_start: yup.number(),
                seconds_end: yup.number().test('after_start', 'Must be after opening time', function (value) {
                    return !this.parent.active || value > this.parent.seconds_start;
                })
            })),
            routing: yup.object({
                team_id: yup.string().required('Choose a team'),
                ring_strategy: yup.string().required(),
                ring_time: yup.number().required(),
                no_answer_destination: yup.string().required()
            }),
            ivr: yup.object({
                enabled: yup.boolean(),
                greeting_script: yup.string().when('enabled', {
                    is: true,
                    then: schema => schema.required('This field is required').max(1000, 'Keep the script under 1000 characters')
                }),
                options: yup.array()
            }),
            voicemail: yup.object({
                greeting_script: yup.string().required('This field is required').max(1000, 'Keep the script under 1000 characters')
            })
        })
    });
    const { fields: businessHoursFields, replace: replaceBusinessHours } = useFieldArray({ control, name: 'business_hours' });
    const { fields: ivrOptionFields, replace: replaceIvrOptions } = useFieldArray({ control, name: 'ivr.options' });

    const values = watch();
    const valuesJSON = JSON.stringify(values);

    const teamOptions = teams.map(({ id, name }) => ({ key: id, value: id, text: name }));
    const inputsJSON = JSON.stringify({
        industry: businessProfile.industry || '',
        team_size: businessProfile.team_size || '',
        teams: teams.map(({ id, name }) => ({ id, name }))
    });
    const isStale = !!generatedFor && JSON.stringify(generatedFor) !== inputsJSON;

    const generateSetup = useCallback(async () => {
        try {
            const inputs = JSON.parse(inputsJSON);
            const result = await dispatch(onboardingActions.generatePhoneSetup({ ...inputs, business_name: businessProfile.business_name }));
            replaceBusinessHours(result.business_hours);
            setValue('routing', result.routing, { shouldValidate: true });
            setValue('ivr.enabled', result.ivr.enabled, { shouldValidate: true });
            setValue('ivr.greeting_script', result.ivr.greeting_script, { shouldValidate: true });
            replaceIvrOptions(result.ivr.options);
            setValue('voicemail', result.voicemail, { shouldValidate: true });
            setExplanations(result.explanations);
            setGeneratedFor(inputs);
            setApproval(null);
        } catch (e) {
            Notification.alert('Failed to generate phone suggestions', STATUS_TYPES.ERROR);
        }
    }, [dispatch, inputsJSON, businessProfile.business_name, replaceBusinessHours, replaceIvrOptions, setValue]);

    // Suggestions are generated automatically the first time the step opens
    const hasSuggestions = !!generatedFor;
    useEffect(() => {
        if (!hasSuggestions) {
            generateSetup();
        }
    }, [hasSuggestions, generateSetup]);

    // An approval only covers what the user saw; any later edit requires approving again
    useEffect(() => {
        if (approval && approval.snapshot !== valuesJSON) {
            setApproval(null);
        }
    }, [approval, valuesJSON]);

    useEffect(() => {
        onDraftChange({
            screen,
            values: JSON.parse(valuesJSON),
            explanations,
            generated_for: generatedFor,
            approval
        });
    }, [onDraftChange, screen, valuesJSON, explanations, generatedFor, approval]);

    // Timezone is saved on the company (Settings → Schedule), so start from it when it's set
    useEffect(() => {
        if (!isCompanyLoaded) {
            dispatch(companyActions.readCompany()).catch(() => {});
        }
    }, [isCompanyLoaded, dispatch]);
    useEffect(() => {
        if (!getValues('timezone') && timezoneOptions.some(({ value }) => value === companyTimezone)) {
            setValue('timezone', companyTimezone, { shouldValidate: true });
        }
    }, [companyTimezone, getValues, setValue]);

    const onRegenerate = async () => {
        const shouldRegenerate = await isConfirmed(
            'Regenerate phone suggestions?',
            'New suggestions will replace your business hours, routing and greeting scripts. Your timezone is kept.'
        );
        if (shouldRegenerate) {
            await generateSetup();
        }
    }

    const onRedraftGreetings = async () => {
        const shouldRedraft = await isConfirmed('Redraft greetings?', 'This replaces both greeting scripts with new drafts based on your current hours and menu.');
        if (!shouldRedraft) {
            return;
        }
        try {
            const { ivr, business_hours } = getValues();
            const greetings = await dispatch(onboardingActions.draftGreetings({
                business_name: businessProfile.business_name,
                hours_summary: summarizeBusinessHours(business_hours),
                ivr_options: ivr.enabled ? ivr.options.map(({ digit, team_id }) => ({ digit, team_name: teamOptions.find(({ value }) => value === team_id)?.text })) : []
            }));
            setValue('ivr.greeting_script', greetings.ivr_greeting_script, { shouldValidate: true });
            setValue('voicemail.greeting_script', greetings.voicemail_greeting_script, { shouldValidate: true });
        } catch (e) {
            Notification.alert('Failed to draft greetings', STATUS_TYPES.ERROR);
        }
    }

    const hasNoOpenDays = businessHoursFields.length > 0 && !(values.business_hours || []).some(({ active }) => active);
    const duplicateDigits = values.ivr?.enabled ? getDuplicateIndexes((values.ivr.options || []).map(({ digit }) => digit)) : [];

    const onContinueToRouting = async () => {
        const isValid = await trigger(BUSINESS_HOURS_FIELDS);
        if (isValid && !hasNoOpenDays) {
            setScreen(PHONE_SYSTEM_SCREENS.ROUTING);
        }
    }

    const onContinueToConfirm = async () => {
        // With a menu, every team is reachable from it, so the single ring team isn't needed
        if (values.ivr.enabled && !values.routing.team_id) {
            setValue('routing.team_id', teamOptions[0]?.value || '');
        }
        const isValid = await trigger(ROUTING_FIELDS);
        if (isValid && !duplicateDigits.length) {
            setScreen(PHONE_SYSTEM_SCREENS.CONFIRM);
        }
    }

    const onConfirm = () => {
        // TODO: send the approved phone setup to the backend once the endpoint exists
        const currentValues = getValues();
        const newApproval = { approved_by: user_id, approved_at: new Date().toISOString(), snapshot: JSON.stringify(currentValues) };
        setApproval(newApproval);
        onComplete({
            screen,
            values: currentValues,
            explanations,
            generated_for: generatedFor,
            approval: newApproval
        });
        Notification.alert('Phone setup saved as a draft', STATUS_TYPES.SUCCESS);
    }

    if (!hasSuggestions) {
        return (
            <Segment placeholder textAlign='center'>
                {isGeneratingPhoneSetup ? (
                    <Loader active inline='centered'>Suggesting hours, routing and greetings for your business…</Loader>
                ) : (
                    <p>We couldn't generate phone suggestions. <Button basic size='small' onClick={generateSetup}>Try Again</Button></p>
                )}
            </Segment>
        );
    }

    switch (screen) {
        case PHONE_SYSTEM_SCREENS.ROUTING:
            return (
                <RoutingAndVoicemail
                    control={control}
                    teamOptions={teamOptions}
                    isIvrEnabled={!!values.ivr?.enabled}
                    ivrOptionFields={ivrOptionFields}
                    duplicateDigits={duplicateDigits}
                    explanations={explanations}
                    isDraftingGreetings={isDraftingGreetings}
                    onRedraftGreetings={onRedraftGreetings}
                    onBack={() => setScreen(PHONE_SYSTEM_SCREENS.HOURS)}
                    onContinue={onContinueToConfirm}
                />
            );
        case PHONE_SYSTEM_SCREENS.CONFIRM:
            return (
                <ConfirmPhoneSetup
                    values={values}
                    teamOptions={teamOptions}
                    isApprovedInitially={!!approval}
                    onBack={() => setScreen(PHONE_SYSTEM_SCREENS.ROUTING)}
                    onConfirm={onConfirm}
                />
            );
        default:
            return (
                <Segment basic style={{ padding: 0 }} loading={isGeneratingPhoneSetup}>
                    <BusinessHoursScreen
                        control={control}
                        businessHoursFields={businessHoursFields}
                        businessHours={values.business_hours || []}
                        hasNoOpenDays={hasNoOpenDays}
                        explanation={explanations.business_hours}
                        isStale={isStale}
                        onRegenerate={onRegenerate}
                        onBack={onBack}
                        onContinue={onContinueToRouting}
                    />
                </Segment>
            );
    }
}

export default PhoneSystemStep;
